// Parses a free-text Medication.frequency string into time-of-day
// buckets for the pillbox visualization. Conservative: when the
// frequency is ambiguous, return the medication in every bucket so
// the user notices instead of missing a dose.

import type { Medication } from '@medreview/shared';

export type TimeSlot = 'morning' | 'noon' | 'evening' | 'bedtime';

const SLOTS: TimeSlot[] = ['morning', 'noon', 'evening', 'bedtime'];

export const TIME_SLOT_LABEL: Record<TimeSlot, string> = {
  morning: 'Morning',
  noon: 'Noon',
  evening: 'Evening',
  bedtime: 'Bedtime',
};

const KEYWORDS: Record<TimeSlot, RegExp[]> = {
  morning: [/\bmorning\b/i, /\bbreakfast\b/i, /\bam\b/i, /\bdawn\b/i, /\bwake\b/i],
  noon: [/\bnoon\b/i, /\blunch\b/i, /\bmidday\b/i],
  evening: [/\bevening\b/i, /\bsupper\b/i, /\bdinner\b/i, /\bpm\b/i],
  bedtime: [/\bbedtime\b/i, /\bnight\b/i, /\bsleep\b/i, /\bovernight\b/i, /\bhs\b/i],
};

const ONCE_DAILY_RE = /\b(once|one time)\s*(a|per)\s*day\b|\b(daily|every day|qd)\b/i;
const TWICE_DAILY_RE = /\b(twice|two times)\s*(a|per)\s*day\b|\b(bid|b\.i\.d|two times daily)\b/i;
const THRICE_DAILY_RE = /\b(three times)\s*(a|per)\s*day\b|\b(tid|t\.i\.d|three times daily)\b/i;
const FOUR_DAILY_RE = /\b(four times)\s*(a|per)\s*day\b|\b(qid|q\.i\.d|four times daily)\b/i;
const EVERY_N_HOURS_RE = /\bevery\s+(\d+)\s+hours?\b/i;

export function timeSlotsFor(frequency: string | undefined): Set<TimeSlot> {
  if (!frequency) return new Set(SLOTS);
  const slots = new Set<TimeSlot>();

  for (const slot of SLOTS) {
    if (KEYWORDS[slot].some((re) => re.test(frequency))) slots.add(slot);
  }

  // If keyword scan caught anything, return that.
  if (slots.size > 0) return slots;

  if (ONCE_DAILY_RE.test(frequency)) {
    slots.add('morning');
    return slots;
  }
  if (TWICE_DAILY_RE.test(frequency)) {
    slots.add('morning');
    slots.add('evening');
    return slots;
  }
  if (THRICE_DAILY_RE.test(frequency)) {
    slots.add('morning');
    slots.add('noon');
    slots.add('evening');
    return slots;
  }
  if (FOUR_DAILY_RE.test(frequency)) {
    slots.add('morning');
    slots.add('noon');
    slots.add('evening');
    slots.add('bedtime');
    return slots;
  }
  const everyHours = frequency.match(EVERY_N_HOURS_RE);
  if (everyHours?.[1]) {
    const hours = Number(everyHours[1]);
    if (hours <= 6) return new Set(SLOTS);
    if (hours <= 8) {
      slots.add('morning');
      slots.add('noon');
      slots.add('evening');
      return slots;
    }
    if (hours <= 12) {
      slots.add('morning');
      slots.add('evening');
      return slots;
    }
    slots.add('morning');
    return slots;
  }
  // Unknown / PRN — show in every compartment as a "check the label" nudge.
  return new Set(SLOTS);
}

export interface BucketedMed {
  med: Medication;
  initial: string;
}

export function bucketize(meds: Medication[]): Record<TimeSlot, BucketedMed[]> {
  const out: Record<TimeSlot, BucketedMed[]> = {
    morning: [],
    noon: [],
    evening: [],
    bedtime: [],
  };
  for (const med of meds) {
    if (med.status !== 'ACTIVE') continue;
    const initial = (med.name || '?').trim().charAt(0).toUpperCase();
    const slots = timeSlotsFor(med.frequency);
    for (const s of slots) out[s].push({ med, initial });
  }
  return out;
}

export const PILL_SLOTS = SLOTS;
